// VisitApprovalDetailPage.js — FINAL FIX
// - Works for: Approvals screen + Dashboard
// - Role normalization (FD_T56 → FRONTDESK)
// - No window.location.reload (prevents logout after check-in/out)

import React, { useEffect, useState, useContext, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axiosClient from "../../../api/axiosClient";
import { AuthContext } from "../../../context/AuthContext";

function VisitApprovalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // -----------------------------
  // Detect source: approvals / dashboard
  // -----------------------------
  const queryParams = new URLSearchParams(location.search);
  let source = queryParams.get("source");

  if (!source) {
    // Fallback based on URL path if source query param is missing
    if (location.pathname.includes("/visitor/approvals")) {
      source = "approvals";
    } else if (location.pathname.includes("/visitor/dashboard")) {
      source = "dashboard";
    } else {
      source = "dashboard"; // safe default
    }
  }

  const { activeScope, user } = useContext(AuthContext);

  const tenant_id = activeScope?.tenant_id;
  const location_id = activeScope?.location_id;

  // ---------- ROLE NORMALIZATION ----------
  // Example: FD_T56 → FD → FRONTDESK
  let role_code = activeScope?.role_code || "";

  if (role_code.includes("_")) {
    role_code = role_code.split("_")[0];
  }

  const roleMapping = {
    FD: "FRONTDESK",
    AD: "ADMIN",
    SEC: "SECURITY",
  };

  const normalizedRole = roleMapping[role_code] || role_code.toUpperCase();

  // ---------- WHO IS LOGGED IN ----------
  const loggedInUserName =
    activeScope?.employee_name ||
    activeScope?.full_name ||
    activeScope?.name ||
    activeScope?.username ||
    user?.username ||
    "";

  // ---------- STATE ----------
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---------- LOAD VISIT DETAIL (reusable) ----------
  const fetchVisit = useCallback(
    async (showLoader = true) => {
      if (!id || !tenant_id || !location_id) return;

      if (showLoader) setLoading(true);
      setError(null);

      try {
        const res = await axiosClient.get(`/visitor-management/visits/${id}`, {
          params: { tenant_id, location_id },
        });
        setVisit(res.data?.data);
      } catch (err) {
        console.error("❌ Visit detail load error:", err);
        setError(
          err?.response?.data?.message || "Failed to load visit details."
        );
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [id, tenant_id, location_id]
  );

  useEffect(() => {
    fetchVisit(true);
  }, [fetchVisit]);

  // ---------- ACTIONS ----------
  const handleApprove = async () => {
    const comment = window.prompt("Enter approval comment (optional):") || "";
    try {
      await axiosClient.post(
        `/visitor-management/visits/${id}/approve`,
        { comment },
        { params: { tenant_id, location_id } }
      );
      alert("Visit Approved.");
      navigate(-1);
    } catch (e) {
      console.error("❌ Approve error:", e);
      alert(e.response?.data?.message || "Approval failed.");
    }
  };

  const handleReject = async () => {
    const reason = window.prompt("Enter rejection reason:");
    if (!reason) return;
    try {
      await axiosClient.post(
        `/visitor-management/visits/${id}/reject`,
        { reason },
        { params: { tenant_id, location_id } }
      );
      alert("Visit Rejected.");
      navigate(-1);
    } catch (e) {
      console.error("❌ Reject error:", e);
      alert(e.response?.data?.message || "Rejection failed.");
    }
  };

  const handleCheckIn = async () => {
    try {
      await axiosClient.post(
        `/visitor-management/visits/${id}/checkin`,
        { gate: "MAIN" },
        { params: { tenant_id, location_id } }
      );
      alert("Visitor checked-in.");
      // 🔄 Soft refresh: reload visit detail, no full page reload
      fetchVisit(false);
    } catch (e) {
      console.error("❌ Check-in error:", e);
      alert(e.response?.data?.message || "Check-In failed.");
    }
  };

  const handleCheckOut = async () => {
    try {
      await axiosClient.post(
        `/visitor-management/visits/${id}/checkout`,
        { gate: "MAIN" },
        { params: { tenant_id, location_id } }
      );
      alert("Visitor checked-out.");
      // 🔄 Soft refresh: reload visit detail, no full page reload
      fetchVisit(false);
    } catch (e) {
      console.error("❌ Check-out error:", e);
      alert(e.response?.data?.message || "Check-Out failed.");
    }
  };

  // ---------- LOGIC: CASE 1 & CASE 2 ----------

  // CASE 2 — Dashboard → Gate roles only (Frontdesk / Admin / Security)
  const isGateRole = ["FRONTDESK", "ADMIN", "SECURITY"].includes(
    normalizedRole
  );

  const showCheckInButton =
    source === "dashboard" && isGateRole && visit?.allowCheckIn === true;

  const showCheckOutButton =
    source === "dashboard" && isGateRole && visit?.allowCheckOut === true;

  // CASE 1 — Approvals → Host only
  const isHostUser =
    loggedInUserName?.toLowerCase().trim() ===
    (visit?.host?.name || "").toLowerCase().trim();

  const showApproveReject =
    source === "approvals" && isHostUser && visit?.status === "PENDING";

  // ---------- UI HELPERS ----------
  const statusBadge = (status) => {
    if (!status) return <span className="badge bg-secondary">-</span>;
    const s = status.toUpperCase();

    const classMap = {
      PENDING: "badge bg-warning text-dark",
      APPROVED: "badge bg-success",
      CHECKED_IN: "badge bg-info text-dark",
      CHECKED_OUT: "badge bg-secondary",
      REJECTED: "badge bg-danger",
    };

    return (
      <span className={classMap[s] || "badge bg-light text-dark"}>
        {status}
      </span>
    );
  };

  // ---------- RENDER ----------
  if (loading) return <div className="container py-5">Loading...</div>;
  if (error) return <div className="container py-5 text-danger">{error}</div>;
  if (!visit) return <div className="container py-5">Visit not found.</div>;

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between mb-3">
        <div>
          <h3 className="fw-bold mb-0">Visit Details – #{id}</h3>
          <small className="text-muted">
            Visitor information & activity tracking
          </small>
        </div>

        <div className="d-flex gap-2">
          {/* Case 1 — Approvals (Host Only) */}
          {showApproveReject && (
            <>
              <button className="btn btn-success btn-sm" onClick={handleApprove}>
                Approve
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleReject}>
                Reject
              </button>
            </>
          )}

          {/* Case 2 — Dashboard (Gate Roles Only) */}
          {showCheckInButton && (
            <button className="btn btn-info btn-sm" onClick={handleCheckIn}>
              Check-In
            </button>
          )}

          {showCheckOutButton && (
            <button className="btn btn-dark btn-sm" onClick={handleCheckOut}>
              Check-Out
            </button>
          )}

          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>
      </div>

      <hr />

      <div className="row g-4">
        {/* Visitor Info */}
        <div className="col-lg-4">
          <h5 className="fw-semibold mb-3">Visitor Information</h5>
          <p>
            <b>Name:</b> {visit.visitor.name}
          </p>
          <p>
            <b>Mobile:</b> {visit.visitor.mobile}
          </p>
          <p>
            <b>Email:</b> {visit.visitor.email}
          </p>
          <p>
            <b>Company:</b> {visit.visitor.company}
          </p>
        </div>

        {/* Host Info */}
        <div className="col-lg-4">
          <h5 className="fw-semibold mb-3">Host Information</h5>
          <p>
            <b>Name:</b> {visit.host.name}
          </p>
          <p>
            <b>Department:</b> {visit.host.department}
          </p>
        </div>

        {/* Visit Info */}
        <div className="col-lg-4">
          <h5 className="fw-semibold mb-3">Visit Details</h5>
          <p>
            <b>Status:</b> {statusBadge(visit.status)}
          </p>
          <p>
            <b>Visit Date:</b> {visit.visit.date}
          </p>
          <p>
            <b>Time:</b> {visit.visit.time}
          </p>
          <p>
            <b>Purpose:</b> {visit.visit.purpose}
          </p>
          <p>
            <b>Notes:</b> {visit.visit.notes || "-"}
          </p>
          <p>
            <b>Check-In Time:</b> {visit.checkin_time || "--"}
          </p>
          <p>
            <b>Check-Out Time:</b> {visit.checkout_time || "--"}
          </p>
        </div>
      </div>
    </div>
  );
}

export default VisitApprovalDetailPage;
